<script setup lang="ts">
/**
 * The shape every assistant page shares (ADR 0061).
 *
 * An assistant whose work is a record rather than a conversation opens on
 * its own page: header (eyebrow, title, meta, actions), an optional stage
 * strip with the one next step, tabs, and the record in the tabs. The
 * helper chat sits in the corner (HelperPanel). Pages differ in what the
 * record is; they do not differ in where things are.
 *
 * Width is the page's, not the content's: the box fills its column up to
 * the cap whatever the current tab holds, so switching tabs never moves
 * the page.
 */
export interface PageTab {
  key: string;
  label: string;
  /** Shown as a small counter; falsy hides it. */
  count?: number | string;
  /** A marker that something on the tab is waiting ("not decided"). */
  dot?: boolean;
  title?: string;
}
export interface PageStage {
  key: string;
  label: string;
  done: boolean;
}
export interface PageNextStep {
  key: string;
  label: string;
}

withDefaults(
  defineProps<{
    title: string;
    eyebrow?: string;
    subtitle?: string;
    tabs?: PageTab[];
    tab?: string;
    stages?: PageStage[];
    /** The stage whose tab is open, highlighted in the strip. */
    currentStage?: string;
    nextStep?: PageNextStep | null;
    /** Content cap. The catalogue's wide table wants more than the bid page. */
    maxWidth?: string;
  }>(),
  { eyebrow: "", subtitle: "", tabs: () => [], tab: "", stages: () => [], currentStage: "", nextStep: null, maxWidth: "1240px" },
);
const emit = defineEmits<{
  (e: "update:tab", key: string): void;
  (e: "stage", key: string): void;
  (e: "next"): void;
}>();
</script>

<template>
  <main class="ap" :style="{ '--ap-max': maxWidth }">
    <header class="ap__head">
      <div class="ap__title">
        <div class="ap__eyebrow"><slot name="eyebrow">{{ eyebrow }}</slot></div>
        <h1>{{ title }}</h1>
        <p v-if="subtitle || $slots.meta" class="ap__sub"><slot name="meta">{{ subtitle }}</slot></p>
      </div>
      <div v-if="$slots.actions" class="ap__actions"><slot name="actions" /></div>
    </header>

    <slot name="banner" />

    <ol v-if="stages.length" class="ap__stages" aria-label="Status">
      <li
        v-for="s in stages"
        :key="s.key"
        :class="{ done: s.done, cur: s.key === currentStage }"
        role="button"
        tabindex="0"
        @click="emit('stage', s.key)"
        @keydown.enter="emit('stage', s.key)"
      >
        <span class="ap__stagemark">{{ s.done ? "✓" : "○" }}</span>{{ s.label }}
      </li>
      <li v-if="$slots.stagesum" class="ap__stagesum"><slot name="stagesum" /></li>
    </ol>

    <div v-if="nextStep" class="ap__next">
      <span class="ap__muted">Næste skridt</span>
      <button type="button" class="btn btn--primary btn--small" @click="emit('next')">{{ nextStep.label }}</button>
    </div>

    <nav v-if="tabs.length" class="ap__tabs" role="tablist">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        class="ap__tab"
        role="tab"
        :aria-selected="tab === t.key"
        :title="t.title"
        @click="emit('update:tab', t.key)"
      >
        {{ t.label }}<span v-if="t.dot" class="ap__dot" title="Afventer">●</span><span v-if="t.count" class="ap__count">{{ t.count }}</span>
      </button>
    </nav>

    <div class="ap__body"><slot /></div>
  </main>
</template>

<style scoped>
.ap { width: 100%; max-width: var(--ap-max); box-sizing: border-box; margin: 0 auto; padding: var(--space-5, 1rem) var(--space-6, 1.5rem) var(--space-8, 5rem); position: relative; min-height: 100vh; }
@media (max-width: 760px) { .ap { padding-left: var(--space-4, 1rem); padding-right: var(--space-4, 1rem); } }
.ap__head { display: flex; flex-wrap: wrap; gap: var(--space-4, 1rem); align-items: flex-start; justify-content: space-between; margin-bottom: var(--space-4, 1rem); }
.ap__title { flex: 1 1 420px; min-width: 0; }
.ap__eyebrow { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--color-muted); font-weight: 600; margin-bottom: 4px; }
.ap h1 { font-family: var(--font-display, inherit); font-size: 28px; margin: 0; }
.ap__sub { margin: 6px 0 0; color: var(--color-muted); font-size: 13.5px; display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.ap__actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.ap__muted { color: var(--color-muted); font-size: 12.5px; }
.ap__stages { display: flex; gap: 0.25rem; list-style: none; padding: 0; margin: 0.5rem 0 0.75rem; flex-wrap: wrap; align-items: center; }
.ap__stages li { display: flex; align-items: center; gap: 0.3rem; padding: 0.25rem 0.6rem; border-radius: 999px; background: var(--color-surface-alt); color: var(--color-muted); font-size: 0.82rem; cursor: pointer; }
.ap__stages li.done { background: #e6f4ea; color: #15803d; }
.ap__stages li.cur { outline: 2px solid var(--color-accent); outline-offset: 1px; }
.ap__stages li + li:not(.ap__stagesum)::before { content: "→"; margin-right: 0.3rem; color: var(--color-muted); }
.ap__stages li.ap__stagesum { background: transparent; margin-left: auto; cursor: default; }
.ap__stagemark { font-weight: 600; }
.ap__next { display: flex; align-items: center; gap: 0.6rem; margin: 0 0 0.75rem; padding: 0.5rem 0.75rem; border: 1px solid var(--color-border); border-radius: var(--radius-md, 8px); background: var(--color-surface); }
.ap__tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--color-border); margin-bottom: 12px; flex-wrap: wrap; }
.ap__tab { background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer; padding: 9px 14px; font: inherit; font-size: 14px; color: var(--color-muted); }
.ap__tab[aria-selected="true"] { color: var(--color-accent); border-bottom-color: var(--color-accent); font-weight: 600; }
.ap__count { font-family: var(--font-mono, monospace); font-size: 11px; color: var(--color-muted); background: var(--color-surface-alt); border-radius: 8px; padding: 1px 6px; margin-left: 6px; }
.ap__dot { margin-left: 0.3rem; color: #b45309; font-size: 0.6rem; vertical-align: middle; }
.ap__body { width: 100%; min-width: 0; }
</style>

<style>
/* Shared vocabulary for the small tags an assistant page uses: a value the
   record carries (framework, stage, decision, "no folder yet"). Global on
   purpose — the app's `.pill` is the inverse-coloured category tag and
   beat every page-scoped `.pill`, which is how white-on-white happened. */
.apill { display: inline-block; font-size: 0.72rem; line-height: 1.4; border: 1px solid var(--color-border); border-radius: 999px; padding: 0.05rem 0.5rem; color: var(--color-muted); background: var(--color-surface); white-space: nowrap; text-transform: none; }
.apill--stage { background: var(--color-surface-alt); border-color: transparent; color: var(--color-ink, inherit); }
.apill--muted { opacity: 0.75; }
.apill--warn { color: #b45309; border-color: #f0c98a; background: #fff7e6; }
.apill--ok { color: #15803d; border-color: #b7e0c3; background: #e6f4ea; }
</style>
